import { useParams } from "react-router-dom";
import InfoChip from "../components/common/InfoChip";
import { FeeTypeText, OrderStatus, OrderType, Severity } from "../types";
import { useFetchOrderDetailsQuery } from "../store";
import { OrderStatusLabels, OrderTypeLabels } from "../configs";

const OrderDetailsWrapper = () => {
  const { orderId } = useParams();

  if (!orderId)
    return (
      <InfoChip
        severity={Severity.DANGER}
        content="Commande introuvable"
      />
    );

  return <OrderDetails orderId={orderId} />;
};

interface OrderDetailsProps {
  orderId: string;
}

const OrderDetails: React.FC<OrderDetailsProps> = ({ orderId }) => {
  const { data: orderDetails, isFetching } = useFetchOrderDetailsQuery(orderId);

  if (isFetching) return <div>Chargement...</div>;

  if (!orderDetails)
    return (
      <InfoChip
        severity={Severity.DANGER}
        content="Commande introuvable"
      />
    );

  const {
    displayId,
    deliveryAddress,
    status,
    orderType,
    city,
    restaurant,
    cart,
    paymentInfo,
    createdAt,
    updatedAt,
    rider,
    user,
  } = orderDetails;

  // --- Tailwind Classes ---
  const cardClass = "border p-4 rounded-lg shadow-sm";
  const sectionTitleClass = "text-lg font-semibold mb-2 text-primary";
  const modifierClass = "ml-4";
  const priceClass = "ml-auto text-right";

  // --- Order Info ---
  const renderedOrderInfo = (
    <div className={cardClass}>
      <h2 className="text-xl font-bold mb-2 text-primary">
        Commande #{displayId}
      </h2>
      <p>
        <b>Status: </b> {OrderStatusLabels[status as OrderStatus]}
      </p>
      <p>
        <b> Type: </b> {OrderTypeLabels[orderType as OrderType]}
      </p>
      <p>
        <b>Ville:</b> {city?.displayName}
      </p>
      <p>
        <b>Adresse de livraison:</b> {deliveryAddress}
      </p>
      <p>
        <b>Créée le:</b> {createdAt}
      </p>
      <p>
        <b>Dernière mise à jour:</b> {updatedAt}
      </p>
    </div>
  );

  // --- Restaurant Info ---
  const renderedRestaurant = (
    <div className={cardClass}>
      <h3 className={sectionTitleClass}>Restaurant</h3>
      <p>
        <b>Nom:</b> {restaurant?.displayName}
      </p>
      <p>
        <b>Adresse:</b> {restaurant?.address}
      </p>
      <p className="mt-2 font-bold underline">Référant</p>
      <p>
        {restaurant?.referant?.firstName} {restaurant?.referant?.lastName}
      </p>
      <p>
        <b> Téléphone: </b> {restaurant?.referant?.phone}
      </p>
    </div>
  );

  // --- Cart ---
  const renderedCart = (
    <div className={cardClass}>
      <h3 className={sectionTitleClass}>Panier</h3>
      {cart.map((item) => (
        <div
          key={item.itemId}
          className="mb-4"
        >
          <div className="flex justify-between font-semibold">
            <span>
              {item.title} x {item.quantity}
            </span>
            <span className={priceClass}>{item.price.totalPrice.text}</span>
          </div>
          {item.modifierGroups.map((group) => (
            <div
              key={group.modifierGroupId}
              className={modifierClass}
            >
              <p className="italic">{group.title}:</p>
              {group.modifierOptions.map((opt) => (
                <div
                  key={opt.modifierOptionId}
                  className="flex justify-between ml-4"
                >
                  <span>
                    {opt.title} x {opt.quantity}
                  </span>
                  <span className={priceClass}>{opt.price.text}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const renderedFees = paymentInfo.fees.map((fee, index) => (
    <div
      key={index}
      className="flex justify-between"
    >
      <span className="font-semibold"> {FeeTypeText[fee.type]} </span>
      <span className={priceClass}>{fee.value.text}</span>
    </div>
  ));

  // --- Payment Info ---
  const renderedPayment = (
    <div className={cardClass}>
      <h3 className={sectionTitleClass}>Paiement</h3>
      <div className="flex justify-between">
        <span className="font-semibold">Prix de la commande:</span>
        <span className={priceClass}>{paymentInfo.orderPrice.text}</span>
      </div>
      {renderedFees}
      <div className="flex justify-between">
        <span className="font-semibold">Total:</span>
        <span className={priceClass}>{paymentInfo.totalPrice.text}</span>
      </div>
    </div>
  );

  // --- User Info ---
  const renderedUser = (
    <div className={cardClass}>
      <h3 className={sectionTitleClass}>Client</h3>
      <p>
        {user.firstName} {user.lastName}
      </p>
      <p>
        <b> Email: </b> {user.email}
      </p>
      <p>
        <b>Téléphone:</b> {user.phone}
      </p>
    </div>
  );

  // --- Rider Info ---
  const renderedRider = rider && (
    <div className={cardClass}>
      <h3 className={sectionTitleClass}>Livreur</h3>
      <p>
        {rider.firstName} {rider.lastName}
      </p>
      <p>
        <b> Email: </b> {rider.email}
      </p>
      <p>
        <b> Téléphone: </b> {rider.phone}
      </p>
      <p>
        <b> Transport: </b> {rider.transportType}
      </p>
    </div>
  );

  return (
    <div className="w-full max-w-5xl mx-auto p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
      {renderedOrderInfo}
      {renderedRestaurant}
      {renderedCart}
      {renderedPayment}
      {renderedUser}
      {renderedRider}
    </div>
  );
};

export default OrderDetailsWrapper;
